import React from 'react';
import {StyleSheet, View} from 'react-native';
import {GestureHandlerRootView} from 'react-native-gesture-handler'; // required by https://reactnavigation.org/docs/drawer-navigator/#installation
import {SafeAreaView} from 'react-native-safe-area-context';
import {faArrowRightFromBracket} from '@fortawesome/free-solid-svg-icons/faArrowRightFromBracket';
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome';
import {
  createDrawerNavigator,
  DrawerContentComponentProps,
  DrawerContentScrollView,
  DrawerItem,
} from '@react-navigation/drawer';
import {DrawerNavigationHelpers} from '@react-navigation/drawer/lib/typescript/src/types';
import {observer} from 'mobx-react-lite';

import {fontFamily} from '../components/ui-controls/Text';
import config from '../config';
import {handleLogoutPress} from '../helpers/authHelpers';
import useIsLoggedIn from '../hooks/useIsLoggedIn';
import {AppRoute} from '../models/AppRoute';
import type {
  DrawerNavigatorScreenProps,
  DrawerParamList,
} from '../models/navigationTypes';
import SplashScreen from '../screens/SplashScreen';
import {useStores} from '../stores/use-stores';
import theme from '../style/theme';
import {navigateToLiquorShop} from '../utils/giftCards';

import {getRoutesForStoreType} from './routes/routes';
import {RouteSpec, StoreType} from './routes/types';
import BottomTabNavigator from './TabNavigator';
import {isRouteEntryVisibleForLoggedInState} from './util';

const LogoutIcon = ({color}: {color: string}) => (
  <FontAwesomeIcon
    icon={faArrowRightFromBracket}
    size={20}
    color={color}
    style={{
      marginLeft: 8,
    }}
  />
);

const GoBackToStoreDrawerItem = ({
  navigation,
}: {
  navigation: DrawerNavigationHelpers;
}) => {
  const handleNavigateToLiquorShop = () => navigateToLiquorShop(navigation);
  return (
    <DrawerItem
      label="Go back to the store"
      onPress={handleNavigateToLiquorShop}
      activeTintColor={theme.colour.text.drawer.primary}
      inactiveTintColor={theme.colour.text.drawer.primary}
      icon={LogoutIcon}
    />
  );
};

type LogoutDrawerItemProps = {
  isLoggedIn: boolean;
  onLogoutPress: () => void;
};

const sortDrawerRoutesBySortOrder = (a: RouteSpec, b: RouteSpec) =>
  (a.drawerSortOrder || 0) - (b.drawerSortOrder || 0);

const LogoutDrawerItem = ({onLogoutPress, isLoggedIn}: LogoutDrawerItemProps) =>
  isLoggedIn ? (
    <DrawerItem
      label="Log out"
      onPress={onLogoutPress}
      activeTintColor={theme.colour.text.drawer.primary}
      inactiveTintColor={theme.colour.text.drawer.primary}
      icon={LogoutIcon}
    />
  ) : null;

type CustomDrawerContentProps = DrawerContentComponentProps &
  LogoutDrawerItemProps & {
    storeType: StoreType;
    storeLoyaltyEnabled: boolean;
    ecommerceDisabled?: boolean;
  };
const CustomDrawerContent = (props: CustomDrawerContentProps) => (
  <DrawerContentScrollView
    {...props}
    contentContainerStyle={{
      flex: 1,
      flexDirection: 'column',
      justifyContent: 'space-between',
    }}>
    <View>
      {getRoutesForStoreType(props.storeType, props.storeLoyaltyEnabled, props.ecommerceDisabled)
        .filter(({showInDrawer}) => showInDrawer)
        .filter(isRouteEntryVisibleForLoggedInState(props.isLoggedIn))
        .sort(sortDrawerRoutesBySortOrder)
        .map(({name, title, params, icon}) => {
          // DrawerContentComponentProps & DrawerNavigationHelpers should support ParamList typing #9348
          // https://github.com/react-navigation/react-navigation/issues/9348
          const handleDrawerItemPress = () =>
            // @ts-ignore
            props.navigation.navigate(name, {params});
          return (
            <DrawerItem
              key={name}
              label={title || ''}
              onPress={handleDrawerItemPress}
              icon={icon}
              activeTintColor={theme.colour.text.drawer.primary}
              inactiveTintColor={theme.colour.text.drawer.primary}
            />
          );
        })}
    </View>
    {!!(
      config.FF_IS_GIFT_CARDS_ENABLED &&
      props.storeType === StoreType.GiftCardStore
    ) && <GoBackToStoreDrawerItem navigation={props.navigation} />}
    <LogoutDrawerItem
      key={'logout'}
      isLoggedIn={props.isLoggedIn}
      onLogoutPress={props.onLogoutPress}
    />
  </DrawerContentScrollView>
);

const Drawer = createDrawerNavigator<DrawerParamList>();

const DrawerNavigator = observer(({navigation}: DrawerNavigatorScreenProps) => {
  const {globalStore, userStore, shopSelectionStore} = useStores();
  const isLoggedIn = useIsLoggedIn();

  const storeType = shopSelectionStore?.getSelectedStoreType();

  let bottomStyle;
  if (globalStore.safeAreaColour) {
    bottomStyle = [
      styles.bottomMain,
      {backgroundColor: globalStore.safeAreaColour},
    ];
  } else {
    bottomStyle = styles.bottomMain;
  }

  // This didn't work on App.tsx and I ran out of time so doing it here
  if (globalStore.isUserDataInitialising) {
    return <SplashScreen />;
  }

  return (
    <GestureHandlerRootView
      style={{flex: 1}}
      testID="gesture-handler-root-view">
      <SafeAreaView style={bottomStyle} edges={['bottom', 'left', 'right']}>
        <Drawer.Navigator
          screenOptions={{
            headerShown: false,
            drawerStyle: styles.main,
            drawerActiveBackgroundColor: theme.colour.background.drawer,
            drawerActiveTintColor: 'white',
            drawerInactiveTintColor: 'white',
            swipeEnabled: false,
            swipeMinDistance: 0,
          }}
          drawerContent={props => (
            <CustomDrawerContent
              onLogoutPress={() => handleLogoutPress(navigation, userStore)}
              isLoggedIn={isLoggedIn}
              storeType={storeType}
              storeLoyaltyEnabled={shopSelectionStore.loyaltyEnabled}
              ecommerceDisabled={shopSelectionStore.ecommerceDisabled}
              {...props}
            />
          )}>
          <Drawer.Screen
            name={AppRoute.MAIN}
            component={BottomTabNavigator}
            options={{
              drawerLabelStyle: styles.drawerScreenItemLabelStyle,
            }}
          />
        </Drawer.Navigator>
      </SafeAreaView>
    </GestureHandlerRootView>
  );
});

export default DrawerNavigator;

const styles = StyleSheet.create({
  main: {
    backgroundColor: theme.colour.background.drawer,
    flex: 1,
  },
  topMain: {
    flex: 0,
    backgroundColor: theme.colour.background.drawer,
  },
  bottomMain: {
    flex: 1,
    backgroundColor: 'white',
  },
  drawerScreenItemLabelStyle: {
    color: theme.colour.text.drawer.primary,
    fontFamily,
  },
});
